<template>
    <client-only>
        <clients-filters
            v-model="filters"
            :has-active-filters="hasActiveFilters"
            :is-loading-data="isLoadingData || usersLoading"
            :users="users"
            :b2cs="b2cs"
            :membership-levels="membershipLevels"
            @submit="applyFilters"
            @reset="resetFilters"
        />
        <v-card class="clients-table__card" variant="outlined">
            <div class="clients-table__card-header d-flex align-center justify-space-between" style="min-height: 40px">
                <div class="ml-auto">
                    <table-options-modal
                        ref="optionsModal"
                        :table-columns="tableColumns"
                        :fixed-columns-names="['name']"
                        :title="$t('clients_page.configure_table_title')"
                        :is-table-options-loading="isTableOptionsLoading"
                        @change-table-options="changeTableOptions($event)"
                    />
                </div>
            </div>
            <v-data-table-server
                v-model="selected"
                :headers="headers"
                :items="items"
                :items-per-page-options="[10, 15, 50, 100]"
                :loading="isLoading"
                :options="options"
                :items-length="clientsCount"
                disable-sort
                class="elevation-1 table-server-selected"
                data-cy="clients-table"
                show-select
                density="compact"
                color="primary"
                @update:options="onUpdateTableOptions"
            >
                <template v-slot:item.name="{item}">
                    <div class="nowrap">
                        <nuxt-link
                            no-prefetch
                            :to="{name: isMember ? 'member' : 'companyClient', params: {id: item.id}}"
                            :data-cy="`link-client-page-${item.id}`"
                        >
                            <strong>{{ item.name }}</strong>
                        </nuxt-link>
                        <template v-if="item.priority && isCRMExtendedModule && !isAgency && !isTO2">
                            <sup v-if="item.priority === 'VIP'" class="text-error">
                                {{ item.priority }}
                            </sup>
                            <v-icon v-else-if="item.priority === 'HIGH'" color="success" size="small">
                                mdi-arrow-up-thin
                            </v-icon>
                            <v-icon v-else-if="item.priority === 'LOW'" color="orange" size="small">
                                mdi-arrow-down-thin
                            </v-icon>
                        </template>
                    </div>
                </template>
                <template v-slot:item.b2CId="{item}">
                    <span v-if="item.b2CId && b2cs.length">
                        {{ getB2CById(item.b2CId) }}
                    </span>
                </template>
                <template v-slot:item.managerId="{item}">
                    <span v-if="item.managerId && users.length">
                        {{ getManagerById(item.managerId) }}
                    </span>
                </template>
                <template v-slot:item.memberLevel="{item}">
                    <span v-if="item.memberLevel && membershipLevels.length">
                        {{ getMembershipLevel(item.memberLevel) }}
                    </span>
                </template>
                <template v-slot:item.active="{item}">
                    <v-switch
                        v-model="item.active"
                        color="primary"
                        :label="item.active ? $t('active') : $t('inactive')"
                        hide-details
                        :data-cy="`client-active-${item.id}`"
                        @update:model-value="changeActive(item)"
                    />
                </template>
            </v-data-table-server>
        </v-card>
    </client-only>
</template>

<script lang="ts">
    import {Component, Emit, Prop, Vue, Watch} from 'vue-facing-decorator'
    import {clean, clone, isEqual} from '@/utils/helpers'
    import ClientsFilters from '~/components/account/clients/ClientsFilters.vue'
    import TableOptionsModal from '~/components/account/TableOptionsModal.vue'
    import {Months} from '~/utils/types/types'

    const initFilters = () => ({
        name: null,
        managerId: null,
        rangeType: null,
        rangeDateFrom: null,
        rangeDateTo: null,
        email: null,
        b2CId: null,
        countryId: null,
        memberLevel: null,
        reservationsNumberFrom: null,
        reservationsNumberTo: null,
        cityId: null,
        language: null,
        currentBalanceFrom: null,
        currentBalanceTo: null,
        stage: null,
        labels: [],
        loyaltyPointFrom: null,
        loyaltyPointTo: null,
        priority: null,
        active: true,
        pattern: null,
        birthdayMonthFrom: null,
        birthdayMonthTo: null,
        birthdayDayFrom: null,
        birthdayDayTo: null,
    })

    @Component({
        components: {ClientsFilters, TableOptionsModal},
    })
    export class ClientsTable extends Vue {
        @Prop({default: false, type: Boolean}) isMember: boolean
        @Prop({default: [], required: true}) users
        @Prop({default: false, required: true}) usersLoading: boolean
        @Watch('selected')
        onChangeSelectedEmails() {
            const emails: string[] = this.selectedClients.filter(({email}) => email).map(({email}) => email)
            this.changeSelectedEmails(emails)
            this.changeSelectedClients()
        }
        @Emit()
        changeSelectedEmails(emails: string[]): string[] {
            return emails
        }
        @Emit()
        changeSelectedClients(): number[] {
            return this.selected
        }

        loaded: boolean = false
        selected: number[] = []
        hiddenColumns = {
            columns: [],
        }
        filters = initFilters()
        isLoadingData: boolean = false
        isTableOptionsLoading: boolean = false
        b2cs = []
        membershipLevels = []

        async mounted() {
            await this.$store.restored
            await this.getHiddenColumns()
            await this.initData()
            await this.loadPrivateClients()
        }

        async initData(): Promise<void> {
            this.isLoadingData = true
            try {
                await Promise.all([
                    (async () => {
                        this.b2cs = (await this.$api.b2c.get()).b2cs.sort((a, b) => a.name.localeCompare(b.name))
                    })(),
                    (async () => {
                        this.membershipLevels = (await this.$api.membershipLevels.get()).membershipLevels
                    })(),
                ])
            } catch (e) {
                this.b2cs = []
                this.membershipLevels = []
            } finally {
                this.isLoadingData = false
            }
        }

        async changeTableOptions(columns: string[]): Promise<void> {
            try {
                this.isTableOptionsLoading = true
                await this.$api.clientsTableOptions.post({userId: authStore.person.personId, columns})
                this.$toast.success(this.$t('messages.success.table_options_saved'))
                this.hiddenColumns.columns = columns
                this.$refs.optionsModal.closeModal()
            } catch (e) {
                this.$toast.error(this.$t('data_update_fail'))
            } finally {
                this.isTableOptionsLoading = false
            }
        }

        async getHiddenColumns(): Promise<void> {
            try {
                const res = await this.$api.clientsTableOptions.get({userId: authStore.person.personId})
                if (res?.length) this.hiddenColumns = res[0]
            } catch (e) {
                this.hiddenColumns = {columns: []}
            }
        }

        applyFilters(): void {
            this.initOptions()
            this.loadPrivateClients()
        }

        resetFilters(): void {
            this.filters = initFilters()
            this.initOptions()
            this.loadPrivateClients()
        }

        get hasActiveFilters(): boolean {
            return !isEqual(initFilters(), this.filters)
        }

        async changeActive({id, active}): Promise<void> {
            try {
                await this.$api.privateClients.patch(id, {active})
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                active
                    ? this.$toast.success(this.$t('messages.success.client_active'))
                    : this.$toast.success(this.$t('messages.success.client_inactive'))
                await this.loadPrivateClients()
            } catch (e) {
                this.$toast.error(this.$t('data_update_fail'))
            }
        }

        async loadPrivateClients() {
            const options = persistentStore.clientsTableOptions
            const filters = clone(this.filters)
            if (filters.name) {
                filters.pattern = filters.name
                delete filters.name
            }
            if (filters.labels) filters.labels = filters.labels.join(',')
            if (filters.birthdayMonthFrom) filters.birthdayMonthFrom = Months[filters.birthdayMonthFrom]
            if (filters.birthdayMonthTo) filters.birthdayMonthTo = Months[filters.birthdayMonthTo]
            clean(filters)

            await runtimeStore.loadPrivateClients({
                ...filters,
                companyId: authStore.person.companyId,
                limit: options.itemsPerPage,
                offset: options.itemsPerPage * (options.page - 1),
                showClientsWithoutNames: false,
                responseType: 'SHORT',
            })
            this.loaded = true
        }

        initOptions() {
            this.options = {page: 1, itemsPerPage: this.options.itemsPerPage}
        }

        onUpdateTableOptions(options) {
            this.options = options
        }

        get headers(): any[] {
            return this.tableColumns.filter(item => item.show)
        }

        get tableColumns() {
            this.isTableOptionsLoading = true
            let tableColumns = [
                {title: this.$t('name'), value: 'name', show: true},
                {title: 'B2C', value: 'b2CId', show: true},
                {title: this.$t('manager'), value: 'managerId', show: true},
                {title: this.$t('stage'), value: 'stage', show: true},
                {title: this.$t('country'), value: 'countryName', show: true},
                {title: this.$t('city'), value: 'cityName', show: true},
                {title: this.$t('phone'), value: 'phone', show: true},
                {title: this.$t('email'), value: 'email', show: true},
                {title: this.$t('registration_date'), value: 'registrationDate', show: true},
                {title: this.$t('first_order_date'), value: 'firstOrderDate', show: true},
                {title: this.$t('last_order_date'), value: 'lastOrderDate', show: true},
                {title: this.$t('membership_level'), value: 'memberLevel', show: true},
                {title: this.$t('expiration_date'), value: 'expirationDate', show: true},
                {
                    title: this.$t('reservations_number'),
                    value: 'reservationsNumber',
                    show: true,
                    width: '10%',
                    align: 'end',
                },
                {title: this.$t('current_balance'), value: 'currentBalance', show: true, width: '10%', align: 'end'},
                ...(this.$conf.public.reservationFilters.loyaltyPoints
                    ? [{title: this.$t('loyalty'), value: 'totalLoyaltyPoints', show: true, width: '10%', align: 'end'}]
                    : []),
                {title: this.$t('status'), value: 'active', minWidth: '140px', show: true},
            ]

            if (!this.isCRMExtendedModule) {
                tableColumns = tableColumns.filter(({value}) => value !== 'stage')
            }

            if (this.isAgency || this.isTO2) {
                tableColumns = tableColumns.filter(({value}) => value !== 'stage' && value !== 'totalLoyaltyPoints')
            }

            if (this.hiddenColumns?.columns?.length) {
                this.hiddenColumns.columns.forEach(item => {
                    const index = tableColumns.findIndex(el => el.value === item)
                    if (index >= 0) tableColumns[index].show = false
                })
            }
            this.isTableOptionsLoading = false
            return tableColumns
        }

        get items() {
            return runtimeStore.clients.map(client => ({
                id: client.personId,
                name: `${(!this.isMember && client.prefix) || ''} ${client.firstName || ''} ${client.lastName || ''}`,
                b2CId: client.b2CId,
                managerId: client.managerId,
                stage: client.stage ? this.$t(`crmStage.${client.stage}`) : '',
                countryName: client.countryName,
                cityName: client.cityName,
                phone: client.contactPhone,
                email: client.email,
                registrationDate: client.registrationDate ? this.$filters.dateShortFormat(client.registrationDate) : '',
                firstOrderDate: client.firstOrderDate ? this.$filters.dateShortFormat(client.firstOrderDate) : '',
                lastOrderDate: client.lastOrderDate ? this.$filters.dateShortFormat(client.lastOrderDate) : '',
                memberLevel: client.memberLevel,
                expirationDate: client.expirationDate ? this.$filters.dateShortFormat(client.expirationDate) : '',
                reservationsNumber: client.reservationsNumber,
                currentBalance: client.balanceCurrency
                    ? this.$filters.priceFormat(client.balance || '0', client.balanceCurrency)
                    : `${client.balance || '0'}`,
                totalLoyaltyPoints: client.totalLoyaltyPoints,
                active: client.active === 'true',
                priority: client.priority,
            }))
        }

        get isAgency() {
            return authStore.isAgency
        }

        get isTO2() {
            return authStore.isTO2
        }

        get isCRMExtendedModule() {
            return runtimeStore.CRMSettings?.extendedCrm || false
        }

        getB2CById(id: number): string {
            const b2c = this.b2cs?.find(({b2cId}) => b2cId === id)
            return `${b2c?.name || ''}`
        }

        getManagerById(id: number): string {
            const user = this.users?.find(({personId}) => personId === id)
            return `${user?.name || ''}`
        }

        getMembershipLevel(levelId: number): string {
            const membershipLevel = this.membershipLevels?.find(({id}) => id === levelId)
            return membershipLevel?.code ? this.$t(`crm_membership_level.${membershipLevel.code}`) : ''
        }

        get clientsCount(): number {
            return runtimeStore.clientsCount
        }

        get isLoading(): boolean {
            return runtimeStore.loading
        }

        get clientsOnPage(): number {
            return persistentStore.clientsTableOptions.itemsPerPage
        }

        get options() {
            return persistentStore.clientsTableOptions
        }

        set options(options) {
            if (!this.loaded) return
            const prev = persistentStore.clientsTableOptions
            if (!prev || matches(options, prev)) return
            persistentStore.SET_CLIENTS_TABLE_OPTIONS(options)
            this.loadPrivateClients()
        }

        get selectedClients() {
            return this.items.filter(({id}) => this.selected.includes(id))
        }
    }

    export default ClientsTable
</script>
